import Page from "components/common/page/Page";
import { useParams, useSearchParams } from "react-router-dom";
import {
    HarvestAgricultureWorkInput,
    HarvestFormDataQuery,
    HarvestFormDataQueryVariables,
    SaveHarvestMutation,
    SaveHarvestMutationVariables
} from "types/graphql";
import useAppNavigation from "utilities/hooks/useAppNavigate";
import { AppRoutes } from "config/router";
import HarvestFormFields, { HarvestFormType } from "components/entities/agriculture/work/HarvestFormFields";
import { gql } from "@apollo/client";
import { useEffect, useState } from "react";
import { ComponentStateTypes } from "components/common/ComponentState";
import { graph } from "config/axios-context";
import { useTranslation } from "react-i18next";
import useAppContext from "utilities/hooks/useAppContext";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { uuid } from "config/app-constants";
import { getHarvestFormData, getHarvestSaveForm } from "utilities/entities/agriculture/worklogs/work-form-utilities";

type WorkFormPageUrlParams = {
    id?: string,
    template?: string
}

const harvestFormData = gql`
query harvestFormData($id: Int, $templateId: Int) {
    season(where: {active: {eq: true}}) {
        id,
        name,
        active
    }
    harvestAgricultureWork(id: $id) {
        id,
        season {
            id,
            name,
            active
        }
        startDate,
        note,
        name
        id
        workers {
            items {
                person {
                    id,
                    name
                }
                id
                price
            }
        }
        harvests {
          items {
            id
            kg,
            cartQuantity,
            hideFromMultiUpdate
            fruit {
                id,
                name
            }
            harvestFruitGroups {
              items {
                id
                fruitGroup {
                  id
                  name
                }
              }
            }
            harvestFruitCategories {
              items {
                id
                fruitCategory {
                  id
                  name
                }
              }
            }
          }
        }
    }
    harvestAgricultureWorkTemplate(id: $templateId) {
        id,
        season {
            id,
            name,
            active
        }
        startDate,
        note,
        name
        workers {
            items {
                person {
                    id,
                    name
                }
                id
                price
            }
        }
        harvests {
          items {
            id
            kg,
            cartQuantity,
            hideFromMultiUpdate
            fruit {
                id,
                name
            }
            harvestFruitGroups {
              items {
                id
                fruitGroup {
                  id
                  name
                }
              }
            },
            harvestFruitCategories {
              items {
                id
                fruitCategory {
                  id
                  name
                }
              }
            }
          }
        }
    }
}
`;


const saveFormMutation = gql`
mutation SaveHarvest($model: HarvestAgricultureWorkInput!) {
    harvestAgricultureWork(model: $model) {
        id
    }
}
`;

const HarvestsFormPage = () => {
    const [t] = useTranslation();
    const navigate = useAppNavigation();
    const context = useAppContext();
    const params = useParams<WorkFormPageUrlParams>();
    const [searchParams] = useSearchParams();
    const [componentState, setComponentState] = useState<ComponentStateTypes>();
    const [model, setModel] = useState<HarvestFormType>();

    // #region load data
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        const templateId = Number(searchParams.get('template')) || 0;
        const id = Number(params.id) || 0;
        try {
            const response = await graph<HarvestFormDataQuery, HarvestFormDataQueryVariables>({
                query: harvestFormData,
                variables: {
                    id,
                    templateId
                }
            });

            if (response.errors) {
                context.notify('error', t('errors.loading-data'));
                setComponentState('error');
                throw response.errors;
            }

            let newModel: HarvestFormType = {
                id: 0,
                workers: [{
                    id: uuid(),
                }],
                season: response.data!.season!,
                name: `Harvest ${DateUtil.format(new Date(), DateFormats.SHORT)} - ${response.data!.season!.name}`,
                date: new Date(),
                harvests: [{
                    id: uuid(),
                    fruitCategories: [],
                    fruitGroup: []
                }]
            };

            if(response.data?.harvestAgricultureWork || response.data?.harvestAgricultureWorkTemplate) {
                const isEdit = response.data.harvestAgricultureWork ? true : false;
                const work = (response.data.harvestAgricultureWork || response.data.harvestAgricultureWorkTemplate)!;
                newModel = getHarvestFormData(work, isEdit, newModel) || newModel;
            }

            setModel({
                ...model,
                ...newModel
            });
            setComponentState('')
        } catch (error) {
            context.notify('error', t('error.loading-data'));
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load data

    const onSubmit = async (model: HarvestFormType) => {
        let saveModel = getHarvestSaveForm<HarvestAgricultureWorkInput>(model);
        try {
            const response = await graph<SaveHarvestMutation, SaveHarvestMutationVariables>({
                query: saveFormMutation,
                variables: {
                    model: saveModel
                }
            });
    
            if(response.errors) {
                throw response.errors
            }

            navigate(AppRoutes.AGRICULTURE_WORK_VIEW, { id: response.data!.harvestAgricultureWork!.id });
        } catch (error) {
            if(error) {
                context.notify('error', t('form.submit.error'));
                throw error
            }
        }
    }

    return (
        <Page title="pages.agriculture.work.harvest.form" state={componentState}>
            <HarvestFormFields
                model={model}
                name="harvest-template-form"
                onSubmit={onSubmit}
            />
        </Page>
    );
};

export default HarvestsFormPage;
